'use client';

import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { TiptapToolbar } from './TiptapToolbar';

type Props = {
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
};

const WYSIWYGEditor = ({ placeholder, value, onChange }: Props) => {
  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Start typing...',
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'min-h-[200px] px-4 py-3 focus:outline-none',
      },
    },
  });

  // Sync external value changes (e.g. AI enhance button)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [editor, value]);

  if (!editor) {
    return (
      <div className="min-h-[300px] rounded-lg border border-gray-300 p-4">
        <p className="text-gray-500">Loading editor...</p>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-lg border border-gray-300 focus-within:border-brand-secondary focus-within:ring-1 focus-within:ring-brand-secondary">
      <TiptapToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};

export default WYSIWYGEditor;
